import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { feature as topojsonFeature, merge as topojsonMerge } from "topojson-client";

import {
  calculateRecommendations,
  formatNumber,
  getGeoId,
  getModeConfig,
  labelGeo,
  logScale,
  optionText,
  padId,
} from "./geographicTargetingUtils";

const DECK_SCRIPT_ID = "geographic-targeting-deckgl";
const BASE_HEIGHT = 550000;
const HEIGHT_FLOOR = 0.05;
const CAP_RGB = [255, 255, 255];
const NO_DATA = [38, 46, 60];

const VIEWS = {
  read: { longitude: -96.6, latitude: 38.4, zoom: 3.6, pitch: 0, bearing: 0 },
  present: { longitude: -96.6, latitude: 28.8, zoom: 3.68, pitch: 44, bearing: -14 },
};

const BASEMAPS = {
  osm: {
    label: "OSM",
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "OpenStreetMap contributors",
    opacity: 0.82,
  },
};

const RAMPS = {
  momentum: ["#0b1f5e", "#1246a0", "#1976d2", "#00a6d6", "#57c7ff", "#c7f0ff"],
  lq: ["#263238", "#006d77", "#00a878", "#7ac943", "#ffd23f", "#ff7a00"],
  rec: ["#263238", "#006d77", "#00a878", "#7ac943", "#ffd23f", "#ff7a00"],
  oz: ["#003c43", "#007c89", "#00acc1", "#5ee3f0", "#d8fbff"],
};

const HEIGHT_LABEL = {
  score: "Momentum score",
  rec: "Recommendation score",
  lq: "Industry LQ",
  emp: "Industry employment",
  population: "Population",
  flat: "Flat",
};

const COLOR_LABEL = {
  lq: "Industry LQ",
  score: "Momentum rank",
  rec: "Recommendation score",
  oz: "OZ 2.0 eligible tracts",
};

const RAMP_RGB = Object.fromEntries(
  Object.entries(RAMPS).map(([key, colors]) => [key, colors.map(hexToRgb)]),
);

const fmt1 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});


function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rampColor(ramp, t) {
  if (t == null || Number.isNaN(t)) return NO_DATA;
  const index = clamp(Math.floor(clamp(t, 0, 0.9999) * ramp.length), 0, ramp.length - 1);
  return ramp[index];
}

function featureId(level, feature) {
  if (level === "metro") return String(feature.id);
  return level === "state" ? padId(feature.id, 2) : padId(feature.id, 5);
}

function topologyFeatures(topo, objectName) {
  if (!topo?.objects?.[objectName]) return [];
  return topojsonFeature(topo, topo.objects[objectName]).features;
}

function buildMetroFeatures(data) {
  const countyGeometries = data.county_topo?.objects?.counties?.geometries || [];
  const countiesByFips = new Map(
    countyGeometries.map((geometry) => [padId(geometry.id, 5), geometry]),
  );

  return (data.metros || [])
    .map((metro) => {
      const parts = (metro.counties || [])
        .map((fips) => countiesByFips.get(fips))
        .filter(Boolean);
      if (!parts.length) return null;
      return {
        type: "Feature",
        id: metro.cbsa,
        properties: { cbsa: metro.cbsa, name: metro.name },
        geometry: topojsonMerge(data.county_topo, parts),
      };
    })
    .filter(Boolean);
}

function ringArea(ring) {
  let area = 0;
  for (let i = 0, n = ring.length - 1; i < n; i += 1) {
    area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return area / 2;
}

function centroidOf(geometry) {
  const polys = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  let best = null;
  let bestArea = 0;

  polys.forEach((poly) => {
    const area = Math.abs(ringArea(poly[0]));
    if (area > bestArea) {
      bestArea = area;
      best = poly[0];
    }
  });
  if (!best) return null;

  let x = 0;
  let y = 0;
  let area = 0;
  for (let i = 0, n = best.length - 1; i < n; i += 1) {
    const cross = best[i][0] * best[i + 1][1] - best[i + 1][0] * best[i][1];
    area += cross;
    x += (best[i][0] + best[i + 1][0]) * cross;
    y += (best[i][1] + best[i + 1][1]) * cross;
  }
  area *= 0.5;
  if (!area) return best[0];
  return [x / (6 * area), y / (6 * area)];
}

function loadDeckScript() {
  if (window.deck) return Promise.resolve(window.deck);

  const existing = document.getElementById(DECK_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.deck), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = DECK_SCRIPT_ID;
    script.src = "/vendor/deck.gl.min.js";
    script.async = true;
    script.onload = () => resolve(window.deck);
    script.onerror = () => reject(new Error("Deck.gl bundle failed to load."));
    document.head.appendChild(script);
  });
}

function recommendationMap(recommendations) {
  return new Map((recommendations || []).map((row) => [row.key, row]));
}

function industryLookup(data, config, selectedNaics) {
  return new Map(
    (data[config.industryKey] || [])
      .filter((row) => row.naics === selectedNaics)
      .map((row) => [row[config.idField], row]),
  );
}

function rawFeatures(data, level) {
  if (level === "state") return topologyFeatures(data.state_topo, "states");
  if (level === "metro") return buildMetroFeatures(data);
  return topologyFeatures(data.county_topo, "counties");
}

function heightRaw(properties, heightMetric) {
  switch (heightMetric) {
    case "score":
      return properties.score;
    case "rec":
      return properties.rec;
    case "lq":
      return properties.lq;
    case "emp":
      return properties.emp == null ? null : logScale(properties.emp, properties.maxEmp);
    case "population":
      return properties.population == null ? null : logScale(properties.population, properties.maxPop);
    default:
      return null;
  }
}

function heightDomain(items, heightMetric) {
  const values = items
    .map((item) => heightRaw(item, heightMetric))
    .filter((value) => value != null && !Number.isNaN(value));
  if (!values.length) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  return { min, max: max === min ? min + 1 : max };
}

function recTier(score) {
  if (score >= 72) return 1;
  if (score >= 58) return 2;
  if (score >= 44) return 3;
  return 4;
}

function buildRows({
  anchors,
  cam,
  colorMetric,
  data,
  exaggeration,
  heightMetric,
  indexes,
  layers,
  level,
  recommendations,
  selectedNaics,
}) {
  const config = getModeConfig(level);
  const features = rawFeatures(data, level);
  const geoMap = indexes[config.indexKey];
  const indMap = industryLookup(data, config, selectedNaics);
  const recMap = recommendationMap(recommendations);
  const geoRows = data[config.geoKey] || [];
  const maxEmp = Math.max(...[...indMap.values()].map((row) => row.emp || 0), 1);
  const maxPop = Math.max(...geoRows.map((row) => row.population || 0), 1);
  const maxOz = Math.max(
    ...[
      ...(data.oz2_state_summary || []),
      ...(data.oz2_county_summary || []),
      ...(data.oz2_metro_summary || []),
    ].map((row) => row.eligible_tracts || 0),
    1,
  );
  const ozRows =
    level === "state"
      ? data.oz2_state_summary || []
      : level === "metro"
        ? data.oz2_metro_summary || []
        : data.oz2_county_summary || [];
  const ozMap = new Map(
    ozRows.map((row) => [
      row.state_fips || row.county_fips || row.cbsa,
      row,
    ]),
  );

  const initial = features.map((feature) => {
    const id = featureId(level, feature);
    const record = geoMap.get(id);
    const industry = indMap.get(id);
    const rec = recMap.get(id);
    const oz = ozMap.get(id);
    return {
      id,
      feature,
      record,
      industry,
      recommendation: rec,
      properties: {
        id,
        name: record ? record.name : feature.properties?.name || id,
        sub: !record
          ? "not in the ranked universe"
          : level === "state"
            ? `${record.abbr || ""} - pop. ${formatNumber.format(record.population || 0)}`
            : `${record.state || record.states || ""} - pop. ${formatNumber.format(
                record.population || 0,
              )}`,
        rank: record?.rank ?? null,
        score: record?.score ?? null,
        rankChange: record?.rank_change ?? null,
        population: record?.population ?? null,
        lq: industry?.lq ?? null,
        emp: industry?.emp ?? null,
        estab: industry?.estab ?? null,
        industry,
        rec: rec?.recScore ?? null,
        recommendation: rec,
        ozTracts: oz?.eligible_tracts ?? null,
        businessPct: record?.business_pct ?? null,
        gdpPct: record?.gdp_pct ?? null,
        employmentPct: record?.employment_pct ?? null,
        populationPct: record?.population_pct ?? null,
        cnbcScore: record?.cnbc_score ?? null,
        maxRank: config.maxRank,
        maxEmp,
        maxPop,
        maxOz,
        isAnchor: anchors.has(id),
      },
    };
  });

  const domain = heightDomain(initial.map((row) => row.properties), heightMetric);
  const flat = cam === "read" || heightMetric === "flat";

  const heightT = (properties) => {
    if (flat) return 0;
    const value = heightRaw(properties, heightMetric);
    if (value == null || Number.isNaN(value)) return 0;
    return HEIGHT_FLOOR + clamp((value - domain.min) / (domain.max - domain.min), 0, 1) * (1 - HEIGHT_FLOOR);
  };

  const elevation = (properties) => heightT(properties) * BASE_HEIGHT * exaggeration;
  const colorT = (properties) => {
    switch (colorMetric) {
      case "score":
        return properties.rank == null ? null : 1 - properties.rank / properties.maxRank;
      case "rec":
        return properties.rec == null ? null : clamp(properties.rec / 100, 0, 1);
      case "lq":
        return properties.lq == null ? null : clamp(properties.lq / 2.5, 0, 1);
      case "oz":
        return properties.ozTracts == null ? null : clamp(properties.ozTracts / properties.maxOz, 0, 1);
      default:
        return null;
    }
  };
  const fillColor = (properties) => {
    const ramp = RAMP_RGB[colorMetric === "score" ? "momentum" : colorMetric];
    const color = rampColor(ramp, colorT(properties));
    return [...color, color === NO_DATA ? 150 : 248];
  };

  const featureCollection = {
    type: "FeatureCollection",
    features: initial.map((row) => ({
      type: "Feature",
      id: row.id,
      geometry: row.feature.geometry,
      properties: {
        ...row.properties,
        elevation: elevation(row.properties),
        fillColor: fillColor(row.properties),
      },
    })),
  };

  const columns = [];
  featureCollection.features.forEach((feature) => {
    const center = centroidOf(feature.geometry);
    if (!center || heightT(feature.properties) <= 0) return;
    columns.push({
      ...feature.properties,
      position: center,
      radius:
        level === "state"
          ? 92000
          : level === "metro"
            ? 42000
            : 9000,
      elevation: feature.properties.elevation,
      fillColor: feature.properties.fillColor,
    });
  });

  const ranked = featureCollection.features
    .filter((feature) => feature.properties.rec != null)
    .sort((a, b) => b.properties.rec - a.properties.rec);
  const topN = level === "state" ? 12 : level === "metro" ? 35 : 30;
  const topIds = layers.recommend
    ? new Set(ranked.slice(0, topN).map((feature) => feature.id))
    : new Set();
  const capPaths = [];
  featureCollection.features.forEach((feature) => {
    if (!topIds.has(feature.id)) return;
    const z = feature.properties.elevation;
    const polys =
      feature.geometry.type === "Polygon" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
    polys.forEach((poly) => {
      if (poly[0].length < 4) return;
      capPaths.push({ id: feature.id, path: poly[0].map((coord) => [coord[0], coord[1], z]) });
    });
  });

  return {
    capPaths,
    columns,
    featureCollection,
    flat,
    ranked,
    topN,
  };
}

function renderTooltip(properties, selectedNaics, industryLabel, cam, heightMetric, colorMetric) {
  if (!properties) return "";
  const rows = [
    [
      "Momentum",
      properties.rank
        ? `#${formatNumber.format(properties.rank)} of ${formatNumber.format(properties.maxRank)} - ${
            properties.score
          }`
        : "not ranked",
    ],
    [`LQ - ${selectedNaics}`, properties.lq == null ? "-" : fmt1.format(properties.lq)],
    ["Industry jobs", properties.emp == null ? "-" : formatNumber.format(properties.emp)],
  ];
  if (properties.rec != null) rows.push(["Recommendation", fmt1.format(properties.rec)]);
  if (properties.ozTracts != null) rows.push(["OZ 2.0 tracts", formatNumber.format(properties.ozTracts)]);

  return `
    <div class="geographic-targeting-map-tt-name">${properties.name}</div>
    <div class="geographic-targeting-map-tt-sub">${properties.sub}${industryLabel ? ` - ${industryLabel}` : ""}</div>
    ${rows
      .map(([key, value]) => `<div class="geographic-targeting-map-tt-row"><span>${key}</span><b>${value}</b></div>`)
      .join("")}
    <div class="geographic-targeting-map-tt-hint">Height: ${
      cam === "read" ? "off" : HEIGHT_LABEL[heightMetric]
    } - Color: ${COLOR_LABEL[colorMetric]}</div>
  `;
}

export default function GeographicTargetingMap({
  data,
  indexes,
  mode,
  selectedNaics,
  anchors,
  layers,
  recommendations,
  onToggleAnchor,
  onSetMode,
  onSetSelectedNaics,
  onSetAnchors,
  onSetLayers,
}) {
  const mapNodeRef = useRef(null);
  const deckRef = useRef(null);
  const hoverRef = useRef(null);
  const [deckLib, setDeckLib] = useState(() => window.deck || null);
  const [loadError, setLoadError] = useState("");
  const [level, setLevel] = useState(mode);
  const [shape, setShape] = useState("column");
  const [heightMetric, setHeightMetric] = useState("score");
  const [colorMetric, setColorMetric] = useState("lq");
  const [localNaics, setLocalNaics] = useState(selectedNaics);
  const [cam, setCam] = useState("present");
  const [basemap, setBasemap] = useState("osm");
  const [outlines, setOutlines] = useState(true);
  const [topCaps, setTopCaps] = useState(true);
  const [exaggeration, setExaggeration] = useState(1);
  const [sort, setSort] = useState("rec");
  const [focusId, setFocusId] = useState(null);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [resultsCollapsed, setResultsCollapsed] = useState(false);
  const [localAnchors, setLocalAnchors] = useState(() => new Set());
  const [anchorSearch, setAnchorSearch] = useState("");
  const [anchorState, setAnchorState] = useState("");
  const [anchorDropdownOpen, setAnchorDropdownOpen] = useState(false);
  const [localLayers, setLocalLayers] = useState(() => ({
    ranking: true,
    industry: true,
    recommend: true,
    anchorOnly: false,
  }));

  useEffect(() => setLevel(mode), [mode]);
  useEffect(() => setLocalNaics(selectedNaics), [selectedNaics]);
  useEffect(() => {
    setAnchorSearch("");
    setAnchorState("");
    setAnchorDropdownOpen(false);
  }, [level]);

  useEffect(() => {
    let isMounted = true;
    loadDeckScript()
      .then((deck) => {
        if (isMounted) setDeckLib(deck);
      })
      .catch((error) => {
        if (isMounted) setLoadError(error.message);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const effectiveAnchors = useMemo(
    () => (level === mode ? anchors : localAnchors),
    [anchors, level, localAnchors, mode],
  );
  const effectiveLayers = useMemo(
    () =>
      level === mode
        ? {
            ranking: true,
            industry: true,
            recommend: true,
            anchorOnly: false,
            ...layers,
          }
        : localLayers,
    [layers, level, localLayers, mode],
  );

  const localRecommendations = useMemo(() => {
    if (!data || !indexes) return [];
    return calculateRecommendations({
      data,
      indexes,
      mode: level,
      selectedNaics: localNaics,
      anchors: effectiveAnchors,
      layers: effectiveLayers,
      criteria: null,
    });
  }, [data, effectiveAnchors, effectiveLayers, indexes, level, localNaics]);

  const activeRecommendations = level === mode && Array.isArray(recommendations) ? recommendations : localRecommendations;
  const industries = data?.industries || data?.all_industries || [];
  const selectedIndustry = industries.find((industry) => industry.naics === localNaics);
  const modeDetails = getModeConfig(level);
  const geoRows = useMemo(() => {
    if (!data) return [];
    return data[modeDetails.geoKey] || [];
  }, [data, modeDetails.geoKey]);
  const anchorStateOptions = useMemo(
    () => [...(data?.states || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [data?.states],
  );
  const anchorOptions = useMemo(() => {
    const query = anchorSearch.trim().toLowerCase();
    const filteredRows = geoRows
      .filter((row) => level !== "county" || !anchorState || row.state_fips === anchorState)
      .filter((row) => {
        const label = level === "county" ? labelGeo(row) : optionText(level, row);
        return !query || label.toLowerCase().includes(query);
      })
      .sort((a, b) => a.rank - b.rank);
    if (level === "county" && anchorState) return filteredRows.slice(0, query ? 500 : 180);
    return filteredRows.slice(0, query ? 500 : 120);
  }, [anchorSearch, anchorState, geoRows, level]);
  const selectedAnchorRows = useMemo(() => {
    const geoMap = indexes?.[modeDetails.indexKey];
    return [...effectiveAnchors].map((id) => geoMap?.get(id)).filter(Boolean);
  }, [effectiveAnchors, indexes, modeDetails.indexKey]);

  const mapRows = useMemo(() => {
    if (!data || !indexes) return null;
    return buildRows({
      anchors: effectiveAnchors,
      cam,
      colorMetric,
      data,
      exaggeration,
      heightMetric,
      indexes,
      layers: effectiveLayers,
      level,
      recommendations: activeRecommendations,
      selectedNaics: localNaics,
    });
  }, [
    activeRecommendations,
    cam,
    colorMetric,
    data,
    effectiveAnchors,
    exaggeration,
    heightMetric,
    indexes,
    effectiveLayers,
    level,
    localNaics,
  ]);

  const sortedTargets = useMemo(() => {
    if (!mapRows) return [];
    const rows = mapRows.ranked.map((feature) => feature.properties).slice();
    const compare =
      {
        rec: (a, b) => b.rec - a.rec,
        rank: (a, b) => (a.rank || 999999) - (b.rank || 999999),
        lq: (a, b) => (b.lq || 0) - (a.lq || 0),
        emp: (a, b) => (b.emp || 0) - (a.emp || 0),
      }[sort] || ((a, b) => b.rec - a.rec);
    return rows.sort(compare);
  }, [mapRows, sort]);

  const focused = useMemo(
    () => sortedTargets.find((row) => row.id === focusId) || null,
    [focusId, sortedTargets],
  );

  const replaceAnchors = useCallback((nextAnchors) => {
    if (level === mode && onSetAnchors) {
      onSetAnchors(nextAnchors);
      return;
    }
    setLocalAnchors(nextAnchors);
  }, [level, mode, onSetAnchors]);

  const toggleAnchor = useCallback((id) => {
    if (!id) return;
    if (level === mode && !onSetAnchors) {
      onToggleAnchor(id);
      return;
    }
    const next = new Set(effectiveAnchors);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    replaceAnchors(next);
  }, [effectiveAnchors, level, mode, onSetAnchors, onToggleAnchor, replaceAnchors]);

  const toggleMapLayer = useCallback((key) => {
    if (level === mode && onSetLayers) {
      onSetLayers((current) => ({ ...current, [key]: !current[key] }));
      return;
    }
    setLocalLayers((current) => ({ ...current, [key]: !current[key] }));
  }, [level, mode, onSetLayers]);

  const changeLevel = useCallback((nextLevel) => {
    if (!nextLevel || nextLevel === level) return;
    setLevel(nextLevel);
    setFocusId(null);
    setAnchorSearch("");
    setAnchorState("");
    setAnchorDropdownOpen(false);
    if (onSetMode) {
      onSetMode(nextLevel);
      onSetAnchors?.(new Set());
    } else {
      setLocalAnchors(new Set());
    }
  }, [level, onSetAnchors, onSetMode]);

  const changeNaics = useCallback((nextNaics) => {
    setLocalNaics(nextNaics);
    setFocusId(null);
    if (level === mode) {
      onSetSelectedNaics?.(nextNaics);
    }
  }, [level, mode, onSetSelectedNaics]);

  useEffect(() => {
    if (!deckLib || !mapNodeRef.current || deckRef.current) return undefined;

    const { AmbientLight, Deck, DirectionalLight, LightingEffect, MapView } = deckLib;
    const deckParent = mapNodeRef.current.querySelector(".geographic-targeting-deck-wrap");
    deckRef.current = new Deck({
      parent: deckParent,
      views: new MapView({ repeat: false }),
      initialViewState: VIEWS.present,
      controller: { dragRotate: true, touchRotate: true, inertia: 250 },
      effects: [
        new LightingEffect({
          ambient: new AmbientLight({ color: [255, 255, 255], intensity: 1.05 }),
          sun: new DirectionalLight({
            color: [255, 250, 240],
            intensity: 1.7,
            direction: [-1.1, -2.6, -1.4],
          }),
          fill: new DirectionalLight({
            color: [190, 215, 255],
            intensity: 0.55,
            direction: [1.4, 1.6, -1.0],
          }),
        }),
      ],
      parameters: { clearColor: [0.9, 0.93, 0.96, 1] },
      layers: [],
      getTooltip: null,
    });

    return () => {
      deckRef.current?.finalize();
      deckRef.current = null;
    };
  }, [deckLib]);

  useEffect(() => {
    const deckParent = mapNodeRef.current?.querySelector(".geographic-targeting-deck-wrap");
    if (!deckLib || !deckRef.current || !deckParent) return undefined;

    let frame = null;
    const syncDeckSize = () => {
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        deckRef.current?.redraw?.(true);
      });
    };

    syncDeckSize();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncDeckSize);
    observer?.observe(deckParent);
    window.addEventListener("resize", syncDeckSize);

    return () => {
      if (frame != null) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", syncDeckSize);
    };
  }, [deckLib]);

  useEffect(() => {
    const deckInstance = deckRef.current;
    if (!deckInstance || !deckLib) return;

    const view = VIEWS[cam];
    const props = { initialViewState: view };
    if (deckLib.FlyToInterpolator) {
      props.initialViewState = {
        ...view,
        transitionDuration: 700,
        transitionInterpolator: new deckLib.FlyToInterpolator({ speed: 1.6 }),
      };
    }
    deckInstance.setProps(props);
  }, [cam, deckLib]);

  useEffect(() => {
    const deckInstance = deckRef.current;
    if (!deckLib || !deckInstance || !mapRows) return;

    const { BitmapLayer, ColumnLayer, GeoJsonLayer, PathLayer, TileLayer } = deckLib;
    const showTooltip = (info) => {
      const node = hoverRef.current;
      if (!node) return;
      const properties = info.object?.properties || info.object;
      const html = renderTooltip(properties, localNaics, selectedIndustry?.label, cam, heightMetric, colorMetric);
      if (!html) {
        node.hidden = true;
        return;
      }
      node.innerHTML = html;
      node.hidden = false;

      const pad = 14;
      const rect = mapNodeRef.current.getBoundingClientRect();
      const width = node.offsetWidth;
      const height = node.offsetHeight;
      node.style.left = `${Math.min(info.x + pad, rect.width - width - pad)}px`;
      node.style.top = `${Math.min(info.y + pad, rect.height - height - pad)}px`;
    };

    const basemapConfig = BASEMAPS[basemap];
    const basemapLayer =
      !basemapConfig
        ? null
        : new TileLayer({
            id: `geographic-targeting-basemap-${basemap}`,
            data: basemapConfig.url,
            minZoom: 0,
            maxZoom: 12,
            maxRequests: 8,
            refinementStrategy: "best-available",
            tileSize: 256,
            opacity: basemapConfig.opacity,
            renderSubLayers: (props) => {
              const bounds = props.tile.boundingBox;
              return new BitmapLayer(props, {
                data: null,
                image: props.data,
                bounds: [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]],
              });
            },
          });

    const geographyLayer =
      !effectiveLayers.ranking
        ? null
        : shape === "polygon"
        ? new GeoJsonLayer({
            id: "geographic-targeting-polygons-3d",
            data: mapRows.featureCollection,
            pickable: true,
            extruded: !mapRows.flat,
            filled: true,
            stroked: mapRows.flat || outlines,
            wireframe: false,
            getFillColor: (feature) => feature.properties.fillColor,
            getElevation: (feature) => feature.properties.elevation,
            getLineColor: (feature) =>
              feature.properties.isAnchor ? [255, 255, 255, 255] : [245, 248, 252, 178],
            lineWidthMinPixels: level === "state" ? 1.35 : 0.8,
            material: { ambient: 0.5, diffuse: 0.65, shininess: 24, specularColor: [60, 80, 110] },
            transitions: { getElevation: 350, getFillColor: 250 },
            onHover: showTooltip,
            onClick: (info) => {
              const id = info.object?.id || info.object?.properties?.id;
              if (!id) return;
              setFocusId(id);
              if (info.object?.properties?.rank) toggleAnchor(id);
            },
          })
        : new GeoJsonLayer({
            id: "geographic-targeting-footprints-3d",
            data: mapRows.featureCollection,
            pickable: true,
            extruded: false,
            filled: true,
            stroked: outlines,
            getFillColor: (feature) => {
              const color = feature.properties.fillColor;
              return [color[0], color[1], color[2], 140];
            },
            getLineColor: (feature) =>
              feature.properties.isAnchor ? [255, 255, 255, 255] : [34, 49, 68, 185],
            lineWidthMinPixels: level === "state" ? 1.25 : 0.7,
            onHover: showTooltip,
            onClick: (info) => {
              const id = info.object?.id || info.object?.properties?.id;
              if (!id) return;
              setFocusId(id);
              if (info.object?.properties?.rank) toggleAnchor(id);
            },
          });

    const columnLayer =
      shape === "column" && effectiveLayers.industry
        ? new ColumnLayer({
            id: "geographic-targeting-columns-3d",
            data: mapRows.columns,
            diskResolution: 18,
            radius: (row) => row.radius,
            extruded: !mapRows.flat,
            pickable: true,
            elevationScale: 1,
            getPosition: (row) => row.position,
            getFillColor: (row) => row.fillColor,
            getElevation: (row) => row.elevation,
            getLineColor: (row) =>
              topCaps && mapRows.capPaths.some((path) => path.id === row.id)
                ? [...CAP_RGB, 255]
                : [0, 0, 0, 0],
            stroked: true,
            lineWidthMinPixels: 1.4,
            material: { ambient: 0.5, diffuse: 0.7, shininess: 30, specularColor: [70, 90, 120] },
            transitions: { getElevation: 350, getFillColor: 250 },
            onHover: showTooltip,
            onClick: (info) => {
              const id = info.object?.id;
              if (!id) return;
              setFocusId(id);
              if (info.object?.rank) toggleAnchor(id);
            },
          })
        : null;

    const topCapLayer =
      topCaps && effectiveLayers.recommend && shape === "polygon"
        ? new PathLayer({
            id: "geographic-targeting-topcaps-3d",
            data: mapRows.capPaths,
            pickable: false,
            getPath: (row) => row.path,
            getColor: [...CAP_RGB, 225],
            widthUnits: "pixels",
            getWidth: 1.3,
            widthMinPixels: 1.3,
            jointRounded: true,
            capRounded: true,
            parameters: { depthTest: false },
          })
        : null;

    deckInstance.setProps({
      layers: [basemapLayer, geographyLayer, columnLayer, topCapLayer].filter(Boolean),
    });
  }, [
    basemap,
    cam,
    colorMetric,
    deckLib,
    effectiveLayers.industry,
    effectiveLayers.ranking,
    effectiveLayers.recommend,
    heightMetric,
    level,
    mapRows,
    mode,
    outlines,
    shape,
    topCaps,
    toggleAnchor,
    localNaics,
    selectedIndustry?.label,
  ]);

  const switchCamera = (nextCam) => {
    setCam(nextCam);
    if (nextCam === "read") {
      setHeightMetric((current) => current);
    }
  };

  const flyToFeature = (id) => {
    const feature = mapRows?.featureCollection.features.find((item) => item.id === id);
    if (!feature || !deckRef.current) return;
    const center = centroidOf(feature.geometry);
    if (!center) return;
    const view = {
      ...VIEWS[cam],
      longitude: center[0],
      latitude: center[1],
      zoom: level === "state" ? 5 : 6.4,
    };
    deckRef.current.setProps({
      initialViewState: deckLib?.FlyToInterpolator
        ? {
            ...view,
            transitionDuration: 900,
            transitionInterpolator: new deckLib.FlyToInterpolator({ speed: 1.6 }),
          }
        : view,
    });
  };

  const resetView = () => {
    deckRef.current?.setProps({ initialViewState: VIEWS[cam] });
  };

  const rampKey = colorMetric === "score" ? "momentum" : colorMetric;
  const activeBasemap = BASEMAPS[basemap];
  const legendEnds = {
    lq: ["LQ 0", "LQ 2.5+"],
    score: ["worst rank", "best rank"],
    rec: ["0", "100"],
    oz: ["0 tracts", "most tracts"],
  }[colorMetric];

  return (
    <div className="geographic-targeting-map geographic-targeting-map-3d" ref={mapNodeRef}>
      <div className="geographic-targeting-deck-wrap" />
      <div className="geographic-targeting-map-tooltip" hidden ref={hoverRef} />
      {activeBasemap?.attribution && (
        <div className="geographic-targeting-map-attribution">
          &copy; {activeBasemap.attribution}
        </div>
      )}

      {loadError && <div className="geographic-targeting-map-error">{loadError}</div>}

      <button
        className={`geographic-targeting-map-floating-toggle left${
          resultsCollapsed ? " collapsed" : ""
        }`}
        onClick={() => setResultsCollapsed((value) => !value)}
        title={resultsCollapsed ? "Show results" : "Collapse results"}
        type="button"
      >
        {resultsCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </button>

      <button
        className={`geographic-targeting-map-floating-toggle right${
          panelCollapsed ? " collapsed" : ""
        }`}
        onClick={() => setPanelCollapsed((value) => !value)}
        title={panelCollapsed ? "Show panel" : "Collapse panel"}
        type="button"
      >
        {panelCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </button>

      <aside
        className={`geographic-targeting-map-panel geographic-targeting-map-glass${
          panelCollapsed ? " collapsed" : ""
        }`}
      >
        <div className="geographic-targeting-map-grp">
          <label>Geography</label>
          <div className="geographic-targeting-map-segmented">
            {["state", "county", "metro"].map((item) => (
              <button
                className={level === item ? "active" : ""}
                key={item}
                onClick={() => changeLevel(item)}
                type="button"
              >
                {getModeConfig(item).plural}
              </button>
            ))}
          </div>
          <div className="geographic-targeting-map-anchor-control">
            {level === "county" && (
              <select
                aria-label="Anchor state"
                onChange={(event) => {
                  setAnchorState(event.target.value);
                  setAnchorSearch("");
                }}
                value={anchorState}
              >
                <option value="">All states</option>
                {anchorStateOptions.map((state) => (
                  <option key={state.state_fips} value={state.state_fips}>
                    {state.name}
                  </option>
                ))}
              </select>
            )}
            <button
              className="geographic-targeting-map-anchor-trigger"
              onClick={() => setAnchorDropdownOpen((value) => !value)}
              type="button"
            >
              <span>
                {selectedAnchorRows.length
                  ? `${selectedAnchorRows.length} anchor${
                      selectedAnchorRows.length === 1 ? "" : "s"
                    } selected`
                  : "Anchor Geographies"}
              </span>
              <b>{anchorDropdownOpen ? "Close" : "Open"}</b>
            </button>
            {selectedAnchorRows.length > 0 && (
              <div className="geographic-targeting-map-anchor-chips">
                {selectedAnchorRows.slice(0, 2).map((row) => {
                  const id = getGeoId(level, row);
                  return (
                    <button key={id} onClick={() => toggleAnchor(id)} type="button">
                      {level === "county" ? labelGeo(row) : optionText(level, row)}
                    </button>
                  );
                })}
                {selectedAnchorRows.length > 2 && <span>+{selectedAnchorRows.length - 2}</span>}
                <button onClick={() => replaceAnchors(new Set())} type="button">
                  Clear
                </button>
              </div>
            )}
            {anchorDropdownOpen && (
              <div className="geographic-targeting-map-anchor-menu">
                <input
                  onChange={(event) => setAnchorSearch(event.target.value)}
                  placeholder={`Search ${modeDetails.plural.toLowerCase()}`}
                  type="search"
                  value={anchorSearch}
                />
                <div className="geographic-targeting-map-anchor-options">
                  {anchorOptions.map((row) => {
                    const id = getGeoId(level, row);
                    const checked = effectiveAnchors.has(id);
                    return (
                      <button
                        className={checked ? "selected" : ""}
                        key={id}
                        onClick={() => toggleAnchor(id)}
                        type="button"
                      >
                        <i>{checked ? "x" : ""}</i>
                        <span>{level === "county" ? labelGeo(row) : optionText(level, row)}</span>
                      </button>
                    );
                  })}
                  {!anchorOptions.length && <div className="empty">No matching geographies.</div>}
                </div>
              </div>
            )}
          </div>
          <div className="geographic-targeting-map-layer-control">
            <div className="geographic-targeting-map-layer-title">Map layers</div>
            {[
              ["ranking", "Competitiveness Ranking"],
              ["industry", "Industry Concentration"],
              ["recommend", "Recommendation Highlight"],
              ["anchorOnly", "Anchor-Compatible Targets Only"],
            ].map(([key, label]) => (
              <label key={key}>
                <input
                  checked={Boolean(effectiveLayers[key])}
                  onChange={() => toggleMapLayer(key)}
                  type="checkbox"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="geographic-targeting-map-grp">
          <label htmlFor="geographic-targeting-heightMetric">3D height metric</label>
          <select
            disabled={cam === "read"}
            id="geographic-targeting-heightMetric"
            onChange={(event) => setHeightMetric(event.target.value)}
            value={heightMetric}
          >
            <option value="score">Momentum Score</option>
            <option value="rec">Recommendation Score</option>
            <option value="lq">Industry LQ</option>
            <option value="emp">Industry Employment</option>
            <option value="population">Population</option>
            <option value="flat">Flat (2D)</option>
          </select>
          {cam === "read" && <div className="geographic-targeting-map-hint">Off in Read mode.</div>}
        </div>

        <div className="geographic-targeting-map-grp">
          <label htmlFor="geographic-targeting-colorMetric">Color</label>
          <select
            id="geographic-targeting-colorMetric"
            onChange={(event) => setColorMetric(event.target.value)}
            value={colorMetric}
          >
            <option value="lq">Industry LQ</option>
            <option value="score">Momentum rank</option>
            <option value="rec">Recommendation score</option>
            <option value="oz">OZ 2.0 eligible tracts</option>
          </select>
        </div>

        <div className="geographic-targeting-map-grp">
          <label htmlFor="geographic-targeting-industrySelect">Target industry (4-digit NAICS)</label>
          <select
            id="geographic-targeting-industrySelect"
            onChange={(event) => changeNaics(event.target.value)}
            value={localNaics}
          >
            {industries.map((industry) => (
              <option key={industry.naics} value={industry.naics}>
                {industry.naics} - {industry.label}
              </option>
            ))}
          </select>
        </div>

        <div className="geographic-targeting-map-grp">
          <label>Camera</label>
          <div className="geographic-targeting-map-segmented">
            <button className={cam === "read" ? "active" : ""} onClick={() => switchCamera("read")} type="button">
              Read
            </button>
            <button
              className={cam === "present" ? "active" : ""}
              onClick={() => switchCamera("present")}
              type="button"
            >
              Present
            </button>
          </div>
          {cam === "read" && (
            <div className="geographic-targeting-map-hint">
              Straight down with height switched off; color is the only encoding.
            </div>
          )}
        </div>

        <div className="geographic-targeting-map-grp">
          <label>Extrusion</label>
          <div className="geographic-targeting-map-segmented">
            <button
              className={shape === "polygon" ? "active" : ""}
              disabled={cam === "read"}
              onClick={() => setShape("polygon")}
              type="button"
            >
              Polygons
            </button>
            <button
              className={shape === "column" ? "active" : ""}
              disabled={cam === "read"}
              onClick={() => setShape("column")}
              type="button"
            >
              Columns
            </button>
          </div>
        </div>

        <div className="geographic-targeting-map-grp">
          <label htmlFor="geographic-targeting-exaggeration">
            Vertical exaggeration <span>{exaggeration.toFixed(1)}x</span>
          </label>
          <input
            disabled={cam === "read"}
            id="geographic-targeting-exaggeration"
            max="3"
            min="0.2"
            onChange={(event) => setExaggeration(Number.parseFloat(event.target.value))}
            step="0.1"
            type="range"
            value={exaggeration}
          />
        </div>

        <div className="geographic-targeting-map-grp">
          <label>Basemap</label>
          <div className="geographic-targeting-map-segmented small">
            {[...Object.keys(BASEMAPS), "none"].map((item) => (
              <button
                className={basemap === item ? "active" : ""}
                key={item}
                onClick={() => setBasemap(item)}
                type="button"
              >
                {BASEMAPS[item]?.label || item[0].toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="geographic-targeting-map-grp checks">
          <label>
            <input checked={outlines} onChange={(event) => setOutlines(event.target.checked)} type="checkbox" />
            State outlines
          </label>
          <label>
            <input checked={topCaps} onChange={(event) => setTopCaps(event.target.checked)} type="checkbox" />
            Outline top targets
          </label>
        </div>

        <div className="geographic-targeting-map-grp">
          <button className="geographic-targeting-map-btn" onClick={resetView} type="button">
            Reset view
          </button>
        </div>

        <div className="geographic-targeting-map-control-legend">
          <h4>Color - {COLOR_LABEL[colorMetric]}</h4>
          <div className="ramp">
            {RAMPS[rampKey].map((color) => (
              <i key={color} style={{ background: color }} />
            ))}
          </div>
          <div className="ends">
            <span>{legendEnds[0]}</span>
            <span>{legendEnds[1]}</span>
          </div>
          <div className="keyrow">
            <span className="sw white" />
            White cap outline - top {mapRows?.topN || 0} recommendations
          </div>
          <div className="keyrow">
            <span className="sw grey" />
            Grey - no data at this level
          </div>
        </div>
      </aside>

      <aside
        className={`geographic-targeting-map-results geographic-targeting-map-glass${
          resultsCollapsed ? " collapsed" : ""
        }`}
      >
        {focused && (
          <section className="geographic-targeting-map-detail">
            <button onClick={() => setFocusId(null)} type="button">
              x
            </button>
            <h3>{focused.name}</h3>
            <div className="sub">{focused.sub}</div>
            <div className="kpis">
              <div>
                <span>Momentum rank</span>
                <b>{focused.rank ? `#${formatNumber.format(focused.rank)}` : "-"}</b>
              </div>
              <div>
                <span>Score</span>
                <b>{focused.score ?? "-"}</b>
              </div>
            </div>
            <table>
              <tbody>
                <tr>
                  <td>Industry LQ</td>
                  <td>{focused.lq == null ? "-" : fmt1.format(focused.lq)}</td>
                </tr>
                <tr>
                  <td>Industry jobs</td>
                  <td>{focused.emp == null ? "-" : formatNumber.format(focused.emp)}</td>
                </tr>
                <tr>
                  <td>Establishments</td>
                  <td>{focused.estab == null ? "-" : formatNumber.format(focused.estab)}</td>
                </tr>
                <tr>
                  <td>OZ 2.0 eligible tracts</td>
                  <td>{focused.ozTracts == null ? "-" : formatNumber.format(focused.ozTracts)}</td>
                </tr>
              </tbody>
            </table>
          </section>
        )}

        <div className="geographic-targeting-map-results-head">
          <div className="eyebrow">Ranked output</div>
          <h3>{getModeConfig(level).label} targets</h3>
          <p>
            {selectedIndustry
              ? `${selectedIndustry.label} - ranked on industry concentration weighted against momentum.`
              : ""}
          </p>
          <select onChange={(event) => setSort(event.target.value)} value={sort}>
            <option value="rec">Sort: fit score</option>
            <option value="rank">Sort: momentum rank</option>
            <option value="lq">Sort: industry LQ</option>
            <option value="emp">Sort: industry employment</option>
          </select>
        </div>

        <div className="geographic-targeting-map-reclist">
          {sortedTargets.slice(0, 40).map((row, index) => (
            <article
              className={`rec tier-${recTier(row.rec)}${row.id === focusId ? " active" : ""}`}
              key={row.id}
              onClick={() => {
                setFocusId(row.id);
                flyToFeature(row.id);
              }}
            >
              <div className="rec-top">
                <div className="rec-rank">{index + 1}</div>
                <div className="rec-name">
                  <h3>{row.name}</h3>
                  <div className="sub">{row.sub}</div>
                </div>
                <div className="rec-score">
                  <div className="n">{fmt1.format(row.rec)}</div>
                  <div className="l">fit</div>
                </div>
              </div>
              <div className="badges">
                <span className="badge navy">Momentum #{formatNumber.format(row.rank)}</span>
                <span className="badge">Score {row.score}</span>
                <span className={`badge ${row.lq >= 1.25 ? "sage" : ""}`}>LQ {fmt1.format(row.lq || 0)}</span>
                <span className="badge">{formatNumber.format(row.emp || 0)} jobs</span>
                {row.ozTracts ? <span className="badge gold">OZ2 {formatNumber.format(row.ozTracts)}</span> : null}
              </div>
            </article>
          ))}
          {!sortedTargets.length && <div className="empty">Nothing ranks at this industry and geography level.</div>}
        </div>
      </aside>

    </div>
  );
}
